import React, { useCallback, useEffect, useRef, useState } from "react";
import "./RoutineList.scss";
import { BASE_URL } from "../../../utils/config";
import { adminRequest } from "../../../utils/requestMethods";
import { ToastContainer } from "react-toastify";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { CiSearch } from "react-icons/ci";
import { IoIosAddCircle, IoIosMore } from "react-icons/io";
import CustomForm from "../../../components/CustomForm/CustomForm";
import OptionsMenu from "../../../components/OptionsMenu/OptionsMenu";
import NoRecord from "../../../components/NoRecord/NoRecord";
import { IoDownloadOutline } from "react-icons/io5";

const RoutineList = () => {
  const filterForm = {
    course: "",
    semester: "",
    section: "",
    day: "",
    enrollmentSetupId: "",
    routinePeriodId: "",
  };
  const headers = [
    "Subject",
    "Program",
    "Semester",
    "Day",
    "Start Time",
    "End Time",
    "Room",
    "Faculty",
  ];
  const [rows, setRows] = useState([]);
  const [formData, setFormData] = useState(filterForm);
  const [course, setCourse] = useState([]);
  const [semester, setSemester] = useState([]);
  const [sections, setSections] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [routinePeriod, setRoutinePeriod] = useState([]);
  const [visibleMenu, setVisibleMenu] = useState(null);
  const menuRefs = useRef([]);
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState("");

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const fetchData = useCallback(async () => {
    try {
      const response = await adminRequest.post(`${BASE_URL}/routine/list`, {
        program: formData.course,
        semester: formData.semester,
        section: formData.section,
        day: formData.day,
        enrollmentSetupId: formData.enrollmentSetupId,
        routinePeriodId: formData.routinePeriodId,
      });

      const fetchedRows = response.data.data.map((r) => ({
        displayData: [
          r.subject,
          r.program,
          r.semester,
          r.day,
          r.startTime,
          r.endTime,
          r.room,
          r.faculty?.name || "N/A",
        ],
        uuid: r.uuid,
      }));
      setRows(fetchedRows);
    } catch (error) {
      console.error("Error fetching routines:", error);
      setRows([]);
    }
  }, [
    formData.course,
    formData.semester,
    formData.section,
    formData.day,
    formData.enrollmentSetupId,
    formData.routinePeriodId,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (formData.semester) {
      const fetchSection = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/section/list/by/curriculum`,
            {
              curriculumId: formData.semester,
            },
          );
          setSections(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSection();
    }
  }, [formData.semester]);

  useEffect(() => {
    const fetchEnrollment = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/enrollmentSetup/list`,
        );
        setAcademicYears(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchEnrollment();
  }, []);

  useEffect(() => {
    if (formData.enrollmentSetupId) {
      const fetchRoutinePeriod = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/routinePeriod/list/enrollmentSetupId`,
            {
              enrollmentSetupId: formData.enrollmentSetupId,
            },
          );
          setRoutinePeriod(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchRoutinePeriod();
    }
  }, [formData.enrollmentSetupId]);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/course/getNameAndId`,
        );
        setCourse(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchProgram();
  }, []);
  useEffect(() => {
    if (formData.course) {
      const fetchSemester = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/getRunningSemestersById`,
            { id: formData.course },
          );
          setSemester(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSemester();
    }
  }, [formData.course]);

  const handleExportToCSV = async () => {
    try {
      const response = await adminRequest.post(
        `${BASE_URL}/routine/download`,
        {
          program: formData?.program,
          semester: formData?.semester,
          enrollmentSetupId: formData?.enrollmentSetupId,
          timeSlotId: formData?.timeSlotId,
          day: formData?.day,
        },
        {
          responseType: "blob",
        },
      );

      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "class_routine.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV", error);
    }
  };

  const handleSearch = React.useRef(null);

  useEffect(() => {
    handleSearch.current = _.debounce(async (query) => {
      if (!query) {
        fetchData();
        return;
      }
      try {
        const response = await adminRequest.post(`${BASE_URL}/routine/search`, {
          searchKeyword: query,
        });
        const fetchedRows = response.data.data.map((r) => ({
          displayData: [
            r.subject,
            r.program,
            r.semester,
            r.day,
            r.startTime,
            r.endTime,
            r.room,
            r.faculty.name,
          ],
          uuid: r.uuid,
          status: r.status,
        }));
        setRows(fetchedRows);
      } catch (error) {
        console.error(error);
      }
    }, 300);
  }, [fetchData]);

  const handleCreateButtonClick = () => {
    navigate("/setup/routine/create");
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchKeyword(value);
    if (handleSearch.current) {
      handleSearch.current(value);
    }
  };

  const handleMoreClick = (rowIndex) => {
    setVisibleMenu(visibleMenu === rowIndex ? null : rowIndex);
  };

  const getMenuItems = (row) => [
    { link: `/setup/routine/edit/${row.uuid}`, text: "Edit" },
  ];

  const filterFields = [
    {
      name: "enrollmentSetupId",
      label: "Enrollment",
      type: "select",
      value: formData.enrollmentSetupId,
      onChange: handleChange,
      options: [
        { label: "Select Enrollment ", value: "" },
        ...academicYears.map((year) => ({
          label: year.enrollmentBatch,
          value: year.id,
        })),
      ],
    },
    {
      name: "routinePeriodId",
      label: "Routine Period",
      type: "select",
      value: formData.routinePeriodId,
      onChange: handleChange,
      options: [
        { label: "Select Routine Period", value: "" },
        ...routinePeriod.map((year) => ({
          label: `${year.effectiveFrom} to ${year.effectiveTo}`,
          value: year.id,
        })),
      ],
    },
    {
      name: "course",
      label: "Course",
      type: "select",
      value: formData.course,
      onChange: handleChange,
      options: [
        { label: "Select Course", value: "" },
        ...course.map((c) => ({
          label: c.short_name,
          value: c.id,
        })),
      ],
    },
    {
      name: "semester",
      label: "Semester",
      type: "select",
      value: formData.semester,
      onChange: handleChange,
      options: [
        { label: "Select Semester", value: "" },
        ...semester.map((s) => ({
          label: s.semester,
          value: s.id,
        })),
      ],
    },
    {
      name: "section",
      label: "Section",
      type: "select",
      value: formData.section,
      onChange: handleChange,
      options: [
        { label: "Select section", value: "" },
        ...sections.map((s) => ({
          label: s.Section.name,
          value: s.id,
        })),
      ],
    },
    {
      name: "day",
      label: "Day",
      type: "select",
      value: formData.day,
      onChange: handleChange,
      options: [
        { label: "Select Day", value: "" },
        ...[
          "SUNDAY",
          "MONDAY",
          "TUESDAY",
          "WEDNESDAY",
          "THURSDAY",
          "FRIDAY",
          "SATURDAY",
        ].map((day) => ({
          label: day.charAt(0) + day.slice(1).toLowerCase(),
          value: day,
        })),
      ],
    },
  ];

  return (
    <div className="routineListContainer">
      <div className="routineListContents">
        <div className="top">
          <span className="leftTitle">
            <FaArrowLeftLong onClick={handleBackClick} className="icon" />
            Routine List
          </span>
          <span className="rightContents">
            <span className="searchField">
              <input
                type="text"
                name="search"
                placeholder="Search Routine..."
                className="searchInput"
                value={searchKeyword}
                onChange={handleInputChange}
              />
              <span className="searchIcon">
                <CiSearch />
              </span>
            </span>
            <span className="exportButton" onClick={handleExportToCSV}>
              <span className="title">Download</span>
              <span className="csvIcon">
                <IoDownloadOutline />
              </span>
            </span>
            <span className="createButton" onClick={handleCreateButtonClick}>
              <span className="title">Create Routine</span>
              <span className="addIcon">
                <IoIosAddCircle />
              </span>
            </span>
          </span>
        </div>
        <div className="filterForm">
          <CustomForm
            createButtonLabel="Search"
            flexDirection="row"
            showDefaultHeader={false}
            fields={filterFields}
            showCancelButton={false}
            showCloseButton={false}
            showCreateButton={false}
          />
        </div>
        <div className="bottom">
          <table className="listTable">
            <thead>
              <tr className="tableHeaderRow">
                <th></th>
                <th></th>
                {headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows && rows.length > 0 ? (
                rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td
                      style={{ position: "relative" }}
                      ref={(el) => (menuRefs.current[rowIndex] = el)}
                    >
                      <IoIosMore onClick={() => handleMoreClick(rowIndex)} />
                      {visibleMenu === rowIndex && (
                        <OptionsMenu
                          menuItems={getMenuItems(row)}
                          visible={true}
                        />
                      )}
                    </td>
                    <td></td>
                    {Array.isArray(row.displayData) &&
                      row.displayData.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={headers.length + 1}
                    style={{ fontSize: "14px", borderBottom: "none" }}
                  >
                    <NoRecord />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <ToastContainer position="top-center" />
      </div>
    </div>
  );
};

export default RoutineList;
